import { Inject, inject, Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { EMPTY, from, Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { AuthenticationSuccess } from '../store/actions';
import { IAuthService } from '../models/iauth-service.model';
import { AuthSelectors } from '../store';
import { AuthenticationType } from '../models/authentication-type.enum';
import { environment } from '@env';
import { MomentService } from '../../../shared/moment/services/moment.service';

@Injectable()
export abstract class MsalAuthBaseService implements IAuthService, OnDestroy {
  private _momentService = inject(MomentService);

  private _destroy$: Subject<boolean> = new Subject<boolean>();
  private _isInitialized: boolean = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private _msalGuardConfig: MsalGuardConfiguration,
    private _broadcastService: MsalBroadcastService,
    private _msalService: MsalService,
    private _store: Store,
  ) { }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  init(): void {
    if (this._isInitialized) return;
    this._isInitialized = true;
    this.startListeningOnAuthStateChange();
  }

  getToken(): Observable<AuthenticationResult> {
    const account = this.getCurrentAccount();
    if (account == null) {
      // Interactive re-auth is owned by MsalGuard/MsalInterceptor; starting a redirect here as
      // well races them and fails with interaction_in_progress. Stay side-effect free instead.
      return EMPTY;
    }

    const authRequest = this._msalGuardConfig.authRequest as RedirectRequest;
    return from(
      this._msalService.instance.acquireTokenSilent({
        account: account,
        scopes: authRequest.scopes,
        state: authRequest.state,
      }),
    );
  }

  isUserAuthenticated(): Observable<boolean> {
    return this._store.select(AuthSelectors.isAuthenticated);
  }

  logout(): Observable<any> {
    return this._msalService.logoutRedirect();
  }

  getCurrentAccount(): AccountInfo | null {
    const accounts = this._msalService.instance
      .getAllAccounts()
      .filter(x => x.idTokenClaims?.aud == (environment.msalModule.clientID))
      .sort((a, b) => this.getTokenExpDate(b).getTime() - this.getTokenExpDate(a).getTime());
    return accounts[0] ?? this._msalService.instance.getActiveAccount();
  }

  goToLoginPage(): Observable<any> {
    const request: RedirectRequest = {
      ...((this._msalGuardConfig.authRequest as RedirectRequest) ?? { scopes: [] }),
      redirectStartPage: window.location.href,
    };
    return this._msalService.loginRedirect(request);
  }

  abstract getAuthenticationType(): AuthenticationType;

  private startListeningOnAuthStateChange(): void {
    this._broadcastService.inProgress$
      .pipe(
        takeUntil(this._destroy$),
        filter((status: InteractionStatus) => status == InteractionStatus.None),
        filter(_ => location.pathname != '/auth'),
        tap(() => {
          const account = this.getCurrentAccount();

          if (account != null) {
            this._msalService.instance.setActiveAccount(account);
            this._store.dispatch(new AuthenticationSuccess());
          }
          // When no account is present, login is initiated solely by MsalGuard — a second
          // concurrent interaction fails with interaction_in_progress and blocks navigation.
        }),
      )
      .subscribe();
  }

  private getTokenExpDate(account: AccountInfo): Date {
    const claims = account.idTokenClaims as any;
    if (!claims.exp) {
      return this._momentService.now().add(1, 'M').toDate();
    }
    const tokenExpDate = new Date(0);
    tokenExpDate.setUTCSeconds(claims.exp);
    return tokenExpDate;
  }
}
